import { type ReactNode, useEffect, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { useDebugPopupStore } from '@/stores/debug-popup';
import { useWhatsappConfig } from '@/hooks/use-whatsapp-config';
import { pb } from '@/core/api';
import { fireConfetti } from '@/utils/confetti';
import { Modal } from '@/components/ui/modal';
import { PopupBody } from './test-passed-popup-parts';

type User = {
  account_status: string;
  test_passed_at?: string;
  whatsapp_link_sent_at?: string;
};

function shouldShow(u: User | null): boolean {
  if (!u || u.account_status !== 'healthy' || !u.test_passed_at) return false;
  if (!u.whatsapp_link_sent_at) return true;
  // Pass is newer than dismiss — user completed a fresh test cycle after
  // dismissing a prior popup, so re-show.
  return new Date(u.test_passed_at) > new Date(u.whatsapp_link_sent_at);
}

function useDismiss(isDebug: boolean): [boolean, () => Promise<void>] {
  const refreshAuth = useAuthStore((s) => s.refreshAuth);
  const closeDebug = useDebugPopupStore((s) => s.setTestPassedOpen);
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  const dismiss = useCallback(async () => {
    if (isDebug) {
      closeDebug(false);
      return;
    }
    setDismissed(true);
    try {
      await pb.send('/api/team/me/dismiss-whatsapp-popup', { method: 'POST' });
      await refreshAuth();
    } catch {
      /* non-blocking */
    }
    if (!useAuthStore.getState().user?.rules_acknowledged_at) {
      navigate('/game-rules');
    }
  }, [isDebug, refreshAuth, closeDebug, navigate]);
  return [dismissed, dismiss];
}

export function TestPassedPopup(): ReactNode {
  const user = useAuthStore((s) => s.user);
  const wa = useWhatsappConfig();
  const debugOpen = useDebugPopupStore((s) => s.testPassedOpen);
  const [dismissed, handleDismiss] = useDismiss(debugOpen);
  const show = debugOpen || (!dismissed && shouldShow(user));

  useEffect(() => {
    if (show) fireConfetti();
  }, [show]);

  if (!show) return null;
  const waLink = wa?.visible && wa.link ? wa.link : '';

  return (
    <Modal
      open={show}
      onClose={handleDismiss}
      title=""
      size="sm"
      dismissible={false}
    >
      <PopupBody waLink={waLink} onDismiss={handleDismiss} />
    </Modal>
  );
}
